import { styled } from '@linaria/react';
import { Group, Menu, Select, Text } from '@mantine/core';
import { useState } from 'react';
import {
  useDismissRiskInsightMutation,
  useUpdateRiskInsightMutation,
} from '../../api/insights-client/insights-client.hooks';
import type { RiskInsightList } from '../../api/insights-client/insights-client.type';
import { InsightStatus } from '../../api/insights-client/insights-client.type';
import { formatDate } from '../../helpers/date-time-helpers/formatting/formatting';
import { baseBlack, baseWhite, blueBase, inkDark, inkLight } from '../../styles/design-tokens';
import { Icon } from '../../ui-library/icon/icon';
import { getSeverityColor, getSeverityIcon } from './insights-helpers';

type InsightCardProps = {
  insight: RiskInsightList;
  onViewAffectedItems?: (insightId: string) => void;
};

export function InsightCard({ insight, onViewAffectedItems }: InsightCardProps) {
  const [localStatus, setLocalStatus] = useState(insight.status);
  const updateMutation = useUpdateRiskInsightMutation();
  const dismissMutation = useDismissRiskInsightMutation();

  const handleStatusChange = (newStatus: string | null) => {
    if (!newStatus) return;

    setLocalStatus(newStatus as InsightStatus);
    updateMutation.mutate({
      insightId: insight.id,
      data: { status: newStatus as InsightStatus },
    });
  };

  const handleDismiss = () => {
    dismissMutation.mutate(insight.id);
  };

  return (
    <Card>
      <CardLayout>
        <IconContainer>
          <Icon
            name={getSeverityIcon(insight.severity)}
            color={getSeverityColor(insight.severity)}
            variant="filled"
            size={20}
          />
        </IconContainer>

        <ContentArea>
          <Group justify="space-between" align="flex-start" mb={4} wrap="nowrap">
            <Text size="sm" fw={600} c={inkDark} lh={1.4} style={{ flex: 1 }}>
              {insight.message}
            </Text>

            <RightActions>
              <CountBadge>{insight.affected_item_count}</CountBadge>

              <Menu shadow="md" width={140}>
                <Menu.Target>
                  <IconButton aria-label="Insight actions">
                    <Icon name="more_vert" color={inkLight} variant="filled" size={18} />
                  </IconButton>
                </Menu.Target>

                <Menu.Dropdown>
                  <Menu.Item
                    leftSection={<Icon name="visibility_off" variant="filled" size={14} color={inkDark} />}
                    onClick={handleDismiss}
                  >
                    <Text size="xs">Dismiss</Text>
                  </Menu.Item>
                </Menu.Dropdown>
              </Menu>
            </RightActions>
          </Group>

          <Text size="xs" c={inkLight} lh={1.5} mb="sm">
            {insight.recommendation_action}
          </Text>

          <Group justify="space-between" align="center">
            <Group gap="sm" align="center">
              <StatusSelect
                value={localStatus}
                onChange={handleStatusChange}
                data={[
                  { value: InsightStatus.NOT_STARTED, label: 'Not Started' },
                  { value: InsightStatus.IN_PROGRESS, label: 'In Progress' },
                  { value: InsightStatus.COMPLETED, label: 'Completed' },
                ]}
                size="xs"
                w={130}
                comboboxProps={{ shadow: 'md' }}
                styles={{
                  input: {
                    fontSize: '11px',
                    height: '28px',
                    minHeight: '28px',
                    borderRadius: '6px',
                  },
                }}
              />
              <Text size="xs" c={inkLight}>
                Updated {formatDate(insight.metadata_updated_at, { relative: true })}
              </Text>
            </Group>

            <ViewItemsLink onClick={() => onViewAffectedItems?.(insight.id)}>
              View affected items
              <Icon name="arrow_forward" variant="filled" size={12} color={blueBase} />
            </ViewItemsLink>
          </Group>
        </ContentArea>
      </CardLayout>
    </Card>
  );
}

const Card = styled.div`
  background: ${baseWhite};
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  padding: 16px;
  transition: all 0.2s ease;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.08);

  &:hover {
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.12);
    transform: translateY(-1px);
    border-color: #d1d5db;
  }
`;

const CardLayout = styled.div`
  display: flex;
  gap: 12px;
  align-items: flex-start;
`;

const IconContainer = styled.div`
  flex-shrink: 0;
  padding-top: 2px;
`;

const ContentArea = styled.div`
  flex: 1;
  min-width: 0;
`;

const RightActions = styled.div`
  display: flex;
  gap: 8px;
  align-items: center;
  flex-shrink: 0;
`;

const CountBadge = styled.div`
  background: ${baseWhite};
  color: ${baseBlack};
  border: 1px solid ${inkLight};
  font-size: 12px;
  font-weight: 600;
  padding: 4px 10px;
  border-radius: 6px;
  min-width: 32px;
  text-align: center;
  line-height: 1.2;
`;

const IconButton = styled.button`
  background: transparent;
  border: none;
  cursor: pointer;
  padding: 2px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 4px;
  transition: background 0.15s ease;
  flex-shrink: 0;

  &:hover {
    background: #f3f4f6;
  }
`;

const StatusSelect = styled(Select)`
  .mantine-Select-input {
    font-size: 11px;
    height: 28px;
    min-height: 28px;
  }
`;

const ViewItemsLink = styled.button`
  background: transparent;
  border: none;
  cursor: pointer;
  font-size: 11px;
  font-weight: 600;
  color: ${blueBase};
  padding: 0;
  display: inline-flex;
  align-items: center;
  gap: 4px;
  transition: opacity 0.15s ease;

  &:hover {
    opacity: 0.8;
    text-decoration: underline;
  }
`;
